import React, { useState, useEffect } from 'react';
import {
  Send,
  MessageCircle,
  ThumbsUp,
  ThumbsDown,
  Edit,
  Trash2,
  Reply,
  MoreHorizontal,
  Check,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface CommentData {
  id: string;
  content: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  likes: number;
  dislikes: number;
  isLiked: boolean;
  isDisliked: boolean;
  canEdit: boolean;
  canDelete: boolean;
  edited: boolean;
  editedAt?: string;
  timeAgo: string;
  replies?: CommentData[];
}

interface CommentsSectionProps {
  postId: string;
  isOpen: boolean;
  onClose: () => void;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
  postId,
  isOpen,
  onClose,
}) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<CommentData[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 🆕 For delete confirmation modal
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  useEffect(() => {
    if (isOpen && postId) {
      loadComments();
    }
  }, [isOpen, postId]);

  const loadComments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/comments/post/${postId}`);

      if (response.data.success && response.data.data) {
        setComments(response.data.data.comments || []);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      const response = await api.post('/comments', {
        content: newComment.trim(),
        post: postId,
      });

      if (response.data.success && response.data.data?.comment) {
        setComments((prev) => [response.data.data.comment, ...prev]);
        setNewComment('');
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (commentId: string) => {
    try {
      const response = await api.post(`/comments/${commentId}/like`);

      if (response.data.success && response.data.data) {
        const { likes, dislikes, isLiked, isDisliked } = response.data.data;
        updateCommentStats(commentId, { likes, dislikes, isLiked, isDisliked });
      }
    } catch (error) {
      console.error('Failed to like comment:', error);
    }
  };

  const handleDislike = async (commentId: string) => {
    try {
      const response = await api.post(`/comments/${commentId}/dislike`);

      if (response.data.success && response.data.data) {
        const { likes, dislikes, isLiked, isDisliked } = response.data.data;
        updateCommentStats(commentId, { likes, dislikes, isLiked, isDisliked });
      }
    } catch (error) {
      console.error('Failed to dislike comment:', error);
    }
  };

  const updateCommentStats = (
    commentId: string,
    stats: { likes: number; dislikes: number; isLiked: boolean; isDisliked: boolean }
  ) => {
    setComments((prev) =>
      prev.map((comment) => {
        if (comment.id === commentId) {
          return { ...comment, ...stats };
        }
        if (comment.replies) {
          return {
            ...comment,
            replies: comment.replies.map((reply) =>
              reply.id === commentId ? { ...reply, ...stats } : reply
            ),
          };
        }
        return comment;
      })
    );
  };

  // 🆕 New delete flow using modal
  const confirmDelete = (commentId: string) => {
    setDeleteTarget(commentId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const response = await api.delete(`/comments/${deleteTarget}`);
      if (response.data.success) {
        setComments((prev) =>
          prev.filter((comment) => {
            if (comment.id === deleteTarget) return false;
            if (comment.replies) {
              comment.replies = comment.replies.filter(
                (reply) => reply.id !== deleteTarget
              );
            }
            return true;
          })
        );
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
    } finally {
      setDeleteTarget(null);
    }
  };

  const CommentComponent: React.FC<{ comment: CommentData; isReply?: boolean }> = ({
    comment,
    isReply = false,
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(comment.content);
    const [showMenu, setShowMenu] = useState(false);
    const [showReplyInput, setShowReplyInput] = useState(false);
    const [replyContent, setReplyContent] = useState('');

    const handleEdit = async () => {
      if (editContent.trim() && editContent !== comment.content) {
        try {
          const response = await api.put(`/comments/${comment.id}`, {
            content: editContent.trim(),
          });

          if (response.data.success) {
            setComments((prev) =>
              prev.map((c) => {
                if (c.id === comment.id) {
                  return {
                    ...c,
                    content: editContent.trim(),
                    edited: true,
                    editedAt: new Date().toISOString(),
                  };
                }
                if (c.replies) {
                  return {
                    ...c,
                    replies: c.replies.map((r) =>
                      r.id === comment.id
                        ? {
                            ...r,
                            content: editContent.trim(),
                            edited: true,
                            editedAt: new Date().toISOString(),
                          }
                        : r
                    ),
                  };
                }
                return c;
              })
            );
          }
        } catch (error) {
          console.error('Failed to edit comment:', error);
        }
      }
      setIsEditing(false);
    };

    const handleReply = async () => {
      if (!replyContent.trim()) return;

      try {
        const response = await api.post('/comments', {
          content: replyContent.trim(),
          post: postId,
          parentComment: comment.id,
        });

        if (response.data.success && response.data.data?.comment) {
          setComments((prev) =>
            prev.map((c) =>
              c.id === comment.id
                ? {
                    ...c,
                    replies: [...(c.replies || []), response.data.data.comment],
                  }
                : c
            )
          );

          setReplyContent('');
          setShowReplyInput(false);
        }
      } catch (error) {
        console.error('Failed to add reply:', error);
      }
    };

    return (
      <div className={`${isReply ? 'ml-6' : ''}`}>
        <div className="flex items-start space-x-3">
          <div
            className={`${
              isReply ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-xs'
            } rounded-full flex items-center justify-center bg-blue-500 text-white font-medium flex-shrink-0`}
          >
            {comment.author.avatar ? (
              <img
                src={comment.author.avatar}
                alt={comment.author.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              getInitials(comment.author.name)
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-900">
                  {comment.author.name}
                </span>
                <span className="text-xs text-gray-500">{comment.timeAgo}</span>
                {comment.edited && (
                  <span className="text-xs text-gray-400">(edited)</span>
                )}
              </div>

              {(comment.canEdit || comment.canDelete) && (
                <div className="relative">
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>

                  {showMenu && (
                    <div className="absolute right-0 top-6 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
                      {comment.canEdit && (
                        <button
                          onClick={() => {
                            setIsEditing(true);
                            setShowMenu(false);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                      )}
                      {comment.canDelete && (
                        <button
                          onClick={() => {
                            confirmDelete(comment.id);
                            setShowMenu(false);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {isEditing ? (
              <div className="mt-2">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
                <div className="flex justify-end space-x-2 mt-2">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditContent(comment.content);
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleEdit}
                    className="p-1 text-blue-500 hover:text-blue-600"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-1">
                <p className="text-sm text-gray-900">{comment.content}</p>
                
                <div className="flex items-center space-x-4 mt-2">
                  <button
                    onClick={() => handleLike(comment.id)}
                    className={`flex items-center space-x-1 text-xs ${
                      comment.isLiked ? 'text-blue-500' : 'text-gray-500'
                    } hover:text-blue-500`}
                  >
                    <ThumbsUp className="w-3 h-3" />
                    <span>{comment.likes}</span>
                  </button>
                  
                  <button
                    onClick={() => handleDislike(comment.id)}
                    className={`flex items-center space-x-1 text-xs ${
                      comment.isDisliked ? 'text-red-500' : 'text-gray-500'
                    } hover:text-red-500`}
                  >
                    <ThumbsDown className="w-3 h-3" />
                    <span>{comment.dislikes}</span>
                  </button>
                  
                  {!isReply && (
                    <button
                      onClick={() => setShowReplyInput(!showReplyInput)}
                      className="flex items-center space-x-1 text-xs text-gray-500 hover:text-blue-500"
                    >
                      <Reply className="w-3 h-3" />
                      <span>Reply</span>
                    </button>
                  )}
                </div>
                
                {showReplyInput && (
                  <div className="mt-3 ml-6">
                    <div className="flex space-x-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center bg-blue-500 text-white text-xs font-medium flex-shrink-0">
                        {user?.firstName?.[0]}{user?.lastName?.[0]}
                      </div>
                      <div className="flex-1">
                        <textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Write a reply..."
                          className="w-full p-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          rows={2}
                        />
                        <div className="flex justify-end space-x-2 mt-2">
                          <button
                            onClick={() => {
                              setShowReplyInput(false);
                              setReplyContent('');
                            }}
                            className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleReply}
                            disabled={!replyContent.trim()}
                            className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Reply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {comment.replies && comment.replies.length > 0 && (
              <div className="mt-3 space-y-3">
                {comment.replies.map((reply) => (
                  <CommentComponent key={reply.id} comment={reply} isReply={true} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end md:items-center justify-center">
        <div className="bg-white w-full max-w-md mx-4 rounded-t-lg md:rounded-lg max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <MessageCircle className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Comments</h3>
              <span className="text-sm text-gray-500">({comments.length})</span>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Comments List */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-8">
                <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No comments yet</p>
                <p className="text-sm text-gray-400">Be the first to comment!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {comments.map((comment) => (
                  <CommentComponent key={comment.id} comment={comment} />
                ))}
              </div>
            )}
          </div>

          {/* Add Comment Input */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex space-x-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-500 text-white text-sm font-medium flex-shrink-0">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="flex-1">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submitting}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    <span>{submitting ? 'Posting...' : 'Post'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🆕 Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-80 p-5">
            <h3 className="text-lg font-semibold mb-3 text-gray-900">
              Confirm Delete
            </h3>
            <p className="text-sm text-gray-600 mb-5">
              Are you sure you want to delete this comment? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-3 py-1 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-3 py-1 rounded-md bg-red-500 text-white hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommentsSection;
